/**
 * @file usePageVisibility.ts
 * @description Скрыть и показать открытую страницу (phase19.16 Р3а; SB128):
 * обе — право публикации контента, после подтверждения. «Скрыть» — сразу,
 * версии сохраняются (`unpublish`). «Показать» — сразу, если у страницы есть
 * версия на сайте и нет правок (публикация с `show: true` без новой
 * версии, без окна подтверждения — QA Р3а D13); иначе — окно публикации, где
 * у открытой страницы уже отмечены пункт и «Показывать на сайте».
 */

import { useState } from "react";

import { useBuilder } from "../../components/site-builder/builder-context";
import { useNotify } from "../../components/ui/NotifyProvider";
import { pagePublishErrorText } from "../../lib/site-builder/page-publish-errors";

export function usePageVisibility() {
  const b = useBuilder();
  const notify = useNotify();
  const [busy, setBusy] = useState(false);
  const changes = b.publishing.changes;

  async function show() {
    if (!changes) {
      notify.error(b.publishing.error ?? "Черновик страницы ещё не проверен — подождите секунду и повторите.");
      return;
    }
    if (changes.hasChanges || changes.live === null) {
      b.openPublishToShow();
      return;
    }
    setBusy(true);
    try {
      await b.publishing.publish({ show: true });
      notify.success("Страница показана на сайте");
    } catch (err) {
      notify.error(pagePublishErrorText(err, "Не удалось показать страницу."));
    } finally {
      setBusy(false);
    }
  }

  async function hide() {
    const title = b.pages.page?.title ?? "";
    const ok = await b.confirm({
      title: `Скрыть страницу «${title}» с сайта?`,
      description: "Покупатели перестанут её видеть сразу. Версии и черновик сохранятся — показать её можно в любой момент.",
      action: "Скрыть",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await b.pages.unpublish();
      await b.publishing.afterChange();
      notify.success("Страница скрыта с сайта");
    } catch (err) {
      notify.error(pagePublishErrorText(err, "Не удалось скрыть страницу."));
    } finally {
      setBusy(false);
    }
  }

  return { busy, show, hide };
}
